import { Recipe, RecipeIngredient } from '../types/recipe';
import { parseIngredientText, parseNumStr } from './ingredientParser';
import { isNonFoodIngredientLine } from './nonFoodIngredientLine';

/**
 * Shared ingredient preparation for per-step chips (StepIngChips).
 *
 * Cleans ingredient names (strip amounts/units/parentheticals) so chip labels
 * read "2 tbsp · neutral oil" instead of the raw scraped string, while keeping
 * `original` untouched — resolveStepIngredients derives its match candidates
 * from BOTH fields (original covers language mismatches like name "tomatoes"
 * / step "tomates").
 */
const UNIT_RE = /^(tablespoons?|teaspoons?|tbsp|tsp|cups?|pounds?|lbs?|ounces?|oz|grams?|g|kg|ml|liters?|cloves?|slices?|pieces?|packages?|pkgs?|bunches?|heads?|stalks?|sprigs?|cans?|jars?|pinch(es)?|dashes?)\s+/i;

/**
 * A comma tail is prep/filler ("salt, to taste", "onion, halved and sliced")
 * when it starts with one of these — only then is it safe to drop. A comma can
 * also sit inside the name itself ("unsweetened, full-fat canned coconut
 * milk"), where dropping the tail would destroy it.
 */
const PREP_TAIL_RE = /^(to|for|plus|such as|about|or|and|ou|et|thinly|finely|roughly|coarsely|halved|sliced|diced|minced|chopped|grated|peeled|cut|divided|more|at room|optional|hachee?s?|coupee?s?|emincee?s?|ciselee?s?|rapee?s?|pelee?s?|ecrasee?s?|tranchee?s?|epepinee?s?|egouttee?s?|rincee?s?|broyee?s?|effeuillee?s?|finement|grossierement|pour|au gout|en des)\b/i;

/**
 * The tail test is written without diacritics, and until #857 it ran on the
 * accented text: « hachées » never matched `hachee?s?`, so « ail, hachées »
 * kept its tail and the garlic chip went missing from every step of every
 * French recipe (« ail » is only a match candidate as the last word).
 */
const stripDiacritics = (text: string) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export function cleanIngredientName(raw: string): string {
  const base = raw
    .replace(/\s*\(.*?\)/g, '') // parentheticals first so "(1-inch) piece …" exposes its unit word
    .replace(/^[\d\s¼-¾./]+/i, '')
    .replace(UNIT_RE, '')
    // French lines carry the quantity through a preposition ("(2 c. à thé)
    // d'huile d'olive"), so stripping the measurement leaves the preposition
    // stranded at the front of the label.
    .replace(/^(de\s+|d['’]|du\s+|des\s+)/i, '')
    .trim();

  const commaIdx = base.indexOf(',');
  if (commaIdx > 0 && PREP_TAIL_RE.test(stripDiacritics(base.slice(commaIdx + 1).trim()))) {
    return base.slice(0, commaIdx).trim() || raw;
  }
  return base || raw;
}

/**
 * The label a chip should carry, derived from `original` rather than from the
 * stored `name`.
 *
 * `original` is the line as the recipe wrote it and is always trustworthy.
 * `name` is a derived field, and a decade of import paths left plenty of it
 * mangled in the catalog — "2 c. à soupe de sauce Worcestershire" saved with
 * the number stripped but the unit left behind, as "à soupe de sauce
 * Worcestershire". Re-deriving costs nothing and heals those rows on read.
 *
 * This is the same derivation the ingredient list uses (RecipeIngredients),
 * which is why the list has always read correctly while the chips did not.
 */
function displayName(ing: RecipeIngredient): string {
  const source = ing.original || ing.name;
  if (!source) return ing.name;
  const remainder = parseIngredientText(source).remainder.trim();
  return cleanIngredientName(remainder || ing.name || source);
}

/**
 * Drops a unit the ingredient line does not actually carry.
 *
 * "1 paquet de 500 g de gnocchis" reached the catalog as amount 1 + unit "g" —
 * the "g" scavenged from the 500 g printed inside the name. Nothing downstream
 * can tell that pair from a real one, so the imperial toggle dutifully turned a
 * 500 g package into "0.04 oz". Only fires when the number still agrees with
 * the line and the line itself carries no unit there, so a genuine structured
 * quantity is never touched.
 */
function sanitizeQuantity(ing: RecipeIngredient): RecipeIngredient {
  if (!ing.amount || ing.amount <= 0 || !ing.unit || !ing.original) return ing;
  const parsed = parseIngredientText(ing.original);
  if (parsed.unitPart.trim()) return ing;
  const lineAmount = parseNumStr(parsed.numPart);
  if (!lineAmount || Math.abs(lineAmount - ing.amount) > 0.01) return ing;
  return { ...ing, unit: '' };
}

export function buildStepIngredients(recipe: Recipe): RecipeIngredient[] {
  const base: RecipeIngredient[] = recipe.extendedIngredients?.length
    ? recipe.extendedIngredients
    : (recipe.ingredients ?? []).map((raw, i) => ({
        id: i,
        name: raw,
        original: raw,
        amount: 0,
        unit: '',
      }));

  return base
    // A yield line or a nutrition row is not an ingredient, so neither may
    // become a step chip. Filtering the ingredients list alone left "Pour 4
    // personnes" showing under step 01 and in cooking mode (#594).
    .filter(ing => !isNonFoodIngredientLine(ing.original || ing.name))
    .map((ing, i) => ({
      ...sanitizeQuantity(ing),
      id: ing.id ?? i,
      name: displayName(ing),
    }));
}
